import axios from 'axios'
import { SYSTEM_BASE_URL, CHAT_ENDPOINTS } from '../../const/EndPoints'
import { SourceCitation } from './chat'

// Recipient-side Walk With Me API.
//
// These endpoints are public — the unguessable token IS the auth. We use raw
// axios (not the authenticated `api` singleton) so no Bearer token is attached.
// Backend wraps every response in `{ data: {...} }`, so we unwrap manually.

// ─── Walk snapshot ───

export interface WalkSnapshotSource {
  id: number
  title: string
  url: string
}

export interface WalkSnapshotMessage {
  id?: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp?: string
  follow_ups?: string[]
  sources?: WalkSnapshotSource[]
}

export interface WalkSnapshot {
  host_first_name?: string
  messages: WalkSnapshotMessage[]
  active_message?: WalkSnapshotMessage | null
}

export const hydrateWalk = async (token: string): Promise<WalkSnapshot> => {
  const response = await axios.get<{ data: WalkSnapshot }>(
    SYSTEM_BASE_URL + CHAT_ENDPOINTS.VIEW_WALK(token)
  )
  return response.data.data
}

// ─── Walk SSE stream ───
//
// Server emits: start, status, text-delta, response, done, presence, revoked,
// error. Each frame follows `event: <type>\ndata: <json>\n\n`. We keep the
// shapes loose so the UI can degrade gracefully if a field is missing.

export type WalkStreamEvent =
  | { type: 'start'; role?: string; content?: string }
  | { type: 'status'; message: string }
  | { type: 'text-delta'; content: string }
  | {
      type: 'response'
      message_id?: string
      role?: 'user' | 'assistant' | 'system'
      content?: string
      message?: string
      follow_ups?: string[]
      sources?: SourceCitation[]
      timestamp?: string
    }
  | { type: 'done' }
  | { type: 'presence'; status?: 'joined' | 'left'; name?: string }
  | { type: 'revoked' }
  | { type: 'error'; message?: string }

export const openWalkStream = (
  token: string,
  onEvent: (event: WalkStreamEvent) => void,
  options?: { guestName?: string }
): EventSource => {
  const base = SYSTEM_BASE_URL + CHAT_ENDPOINTS.STREAM_WALK(token)
  const url = options?.guestName
    ? `${base}?name=${encodeURIComponent(options.guestName)}`
    : base
  const source = new EventSource(url)

  const forward = (type: WalkStreamEvent['type']) => (evt: MessageEvent) => {
    try {
      const parsed = evt.data ? JSON.parse(evt.data) : {}
      onEvent({ type, ...parsed } as WalkStreamEvent)
    } catch {
      onEvent({ type } as WalkStreamEvent)
    }
  }

  source.addEventListener('start', forward('start'))
  source.addEventListener('status', forward('status'))
  source.addEventListener('text-delta', forward('text-delta'))
  source.addEventListener('response', forward('response'))
  source.addEventListener('done', forward('done'))
  source.addEventListener('presence', forward('presence'))
  source.addEventListener('revoked', forward('revoked'))
  source.addEventListener('error', forward('error'))

  return source
}

// ─── Clip view ───

export interface ClipView {
  question?: string
  answer: string
  host_first?: string
  sources?: WalkSnapshotSource[]
  expires_at: string
}

export const hydrateClip = async (token: string): Promise<ClipView> => {
  const response = await axios.get<{ data: ClipView }>(
    SYSTEM_BASE_URL + CHAT_ENDPOINTS.VIEW_CLIP(token)
  )
  return response.data.data
}
